import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Zap, Clock, AlarmClock, ShieldCheck, GraduationCap, X, Bell, Send } from "lucide-react";

/**
 * Top-of-dashboard Office Manager Activity feed. Works for both admins
 * (endpoint `/workflow-notifications`, sees everyone) and employees
 * (`/me/office-manager-activity`, only their own events). Acknowledged
 * items collapse into a "N dismissed" tally so the feed stays clean but
 * nothing is truly hidden until reload.
 */
const KIND_META = {
  missing_clockout: { icon: Clock, tone: "warn", label: "Missing Clock-Out" },
  all_hours_in: { icon: Clock, tone: "ink", label: "All Hours In" },
  all_hours_in_admin: { icon: Clock, tone: "ink", label: "All Hours In · sent" },
  ot_guard: { icon: AlarmClock, tone: "bad", label: "Overtime Guard" },
  probation_onboarding: { icon: ShieldCheck, tone: "warn", label: "Probation Onboarding" },
  license_expiry: { icon: GraduationCap, tone: "warn", label: "License Expiry" },
};

const TONE_CLASS = {
  ink: "border-ink bg-secondary/40",
  warn: "border-warn bg-warn/10",
  bad: "border-bad bg-bad/10",
};

function timeAgo(iso) {
  const then = new Date(iso).getTime();
  const mins = Math.max(0, Math.floor((Date.now() - then) / 60000));
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  if (mins < 60 * 24) return `${Math.floor(mins / 60)}h ago`;
  return `${Math.floor(mins / (60 * 24))}d ago`;
}

export default function OfficeManagerFeed({ scope = "admin" }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [nudging, setNudging] = useState(null);

  const path = scope === "employee" ? "/me/office-manager-activity" : "/workflow-notifications";

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(path);
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      // Employee endpoint quietly returns [] when no employee_id linked
      setItems([]);
    } finally { setLoading(false); }
  }, [path]);
  useEffect(() => { load(); }, [load]);

  const ack = async (id) => {
    try {
      await api.post(`/workflow-notifications/${id}/ack`);
      setItems((prev) => prev.map((x) => x.id === id ? { ...x, acknowledged: true } : x));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const nudge = async (item) => {
    if (!item.employee_id) return;
    setNudging(item.id);
    try {
      await api.post("/workflow/overtime-guard/nudge", {
        employee_id: item.employee_id, notification_id: item.id,
      });
      toast.success("Clock-Out-Early reminder sent");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    setNudging(null);
  };

  const active = items.filter((x) => !x.acknowledged);
  const dismissed = items.length - active.length;

  return (
    <section className="mb-8" data-testid={`omf-${scope}`}>
      <div className="flex items-baseline justify-between mb-3">
        <div className="overline flex items-center gap-2">
          <Zap className="w-4 h-4" />
          § OFFICE MANAGER ACTIVITY
          {active.length > 0 && (
            <span className="ml-2 px-1.5 py-0.5 bg-warn text-ink text-[10px]" data-testid="omf-badge">
              {active.length}
            </span>
          )}
        </div>
        {dismissed > 0 && <span className="font-mono text-[10px] text-muted-foreground">{dismissed} dismissed</span>}
      </div>

      {loading && <div className="hard-card p-4 font-mono text-xs text-muted-foreground">Loading…</div>}

      {!loading && active.length === 0 && (
        <div className="hard-card p-6 font-mono text-xs text-muted-foreground flex items-center gap-3" data-testid="omf-empty">
          <Bell className="w-4 h-4" /> No alerts right now — everything's caught up.
        </div>
      )}

      {!loading && active.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {active.slice(0, 6).map((item) => {
            const meta = KIND_META[item.kind] || { icon: Bell, tone: "ink", label: item.kind };
            const Icon = meta.icon;
            const canNudge = scope === "admin" && item.kind === "ot_guard" && item.employee_id;
            return (
              <div
                key={item.id}
                data-testid={`omf-item-${item.id}`}
                className={`hard-card p-4 border-l-4 flex items-start gap-3 ${TONE_CLASS[meta.tone]}`}
              >
                <div className="p-2 border-2 border-ink bg-white shrink-0"><Icon className="w-4 h-4" /></div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline justify-between gap-2 mb-1">
                    <div className="font-mono uppercase text-[10px] tracking-widest">{meta.label}</div>
                    <div className="font-mono text-[9px] text-muted-foreground">{timeAgo(item.sent_at)}</div>
                  </div>
                  <div className="font-heading font-black text-sm leading-tight mb-1">{item.title || meta.label}</div>
                  <div className="font-mono text-xs text-muted-foreground mb-2 leading-relaxed">{item.message}</div>
                  {item.employee_name && scope === "admin" && (
                    <div className="font-mono text-[10px] text-muted-foreground mb-2">Employee: <b>{item.employee_name}</b></div>
                  )}
                  <div className="flex items-center gap-2">
                    {canNudge && (
                      <button
                        data-testid={`omf-nudge-${item.id}`}
                        onClick={() => nudge(item)}
                        disabled={nudging === item.id}
                        className="border-2 border-ink bg-ink text-white px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-good inline-flex items-center gap-1 disabled:opacity-40"
                        title="Email employee a Clock-Out-Early reminder"
                      ><Send className="w-3 h-3" /> {nudging === item.id ? "Sending…" : "Nudge Employee"}</button>
                    )}
                    <button
                      data-testid={`omf-ack-${item.id}`}
                      onClick={() => ack(item.id)}
                      className="border-2 border-ink px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-ink hover:text-white inline-flex items-center gap-1"
                    ><X className="w-3 h-3" /> Dismiss</button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
      {!loading && active.length > 6 && (
        <div className="mt-2 font-mono text-[10px] text-muted-foreground">+{active.length - 6} more · dismiss above to reveal</div>
      )}
    </section>
  );
}
